/**
 * Where the edge function sends people.
 *
 * The CloudFront viewer function cannot render a body without spending its
 * 10KB code budget on HTML, so instead of serving an inline error it redirects
 * to the SPA and lets React render a real page. Two cases arrive here:
 *
 *   /expired?s=acme     a page whose hosting window ran out
 *   /?claim=acme        someone visited a subdomain nobody owns
 *
 * The second is a lead, not an error: land them in the builder with the name
 * they just typed already filled in.
 */
import { isValidSlug } from '@simplelaunch/shared'

/** Kept in step with LEGAL_PAGES; both are ours, neither is user input. */
const LEGAL_SLUGS = new Set([
  'terms', 'acceptable-use', 'privacy', 'refunds', 'dmca', 'trademark',
])

/** Kept in step with HOSTING_WINDOWS; validated before it reaches state. */
const WINDOW_IDS = new Set(['window_3mo', 'window_6mo', 'window_12mo'])

export type Entry =
  | { readonly kind: 'landing' }
  | { readonly kind: 'build'; readonly claim?: string; readonly windowId?: string }
  | { readonly kind: 'expired'; readonly slug?: string }
  | { readonly kind: 'legal'; readonly slug: string }

export function readEntry(url: URL): Entry {
  const path = url.pathname.replace(/\/+$/, '')
  const slugParam = (name: string): string | undefined => {
    const value = url.searchParams.get(name)?.toLowerCase().trim()
    // Reflecting an arbitrary query parameter into the page is how a text
    // injection becomes a phishing lure on your own domain.
    return value && isValidSlug(value) ? value : undefined
  }

  if (LEGAL_SLUGS.has(path.replace(/^\//, ''))) {
    return { kind: 'legal', slug: path.replace(/^\//, '') }
  }

  if (path === '/expired') {
    const slug = slugParam('s')
    return slug ? { kind: 'expired', slug } : { kind: 'expired' }
  }
  const claim = slugParam('claim')
  const windowId = WINDOW_IDS.has(url.searchParams.get('w') ?? '')
    ? (url.searchParams.get('w') as string)
    : undefined

  // Someone who typed an unclaimed subdomain has already chosen a name, so
  // send them straight to the builder rather than to the pitch.
  if (path === '/build' || claim) {
    return {
      kind: 'build',
      ...(claim ? { claim } : {}),
      ...(windowId ? { windowId } : {}),
    }
  }

  return { kind: 'landing' }
}
